import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';
import { 
  Eye, 
  EyeOff, 
  Mail, 
  User, 
  Sparkles, 
  DollarSign, 
  TrendingUp, 
  Target, 
  PieChart, 
  Bot,
  Lock,
  KeyRound,
  ArrowLeft,
  Clock,
  RotateCcw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export const Landing = () => {
  const navigate = useNavigate();
  const { loginWithEmail, registerWithEmail, loginWithGoogle, forgotPassword, resetPassword, isAuthenticated } = useAuth();

  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [subPrice, setSubPrice] = useState(null);
  
  // Forgot / Reset password modal state
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1 = solicitar código, 2 = código e novas senhas
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotCode, setForgotCode] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [showForgotNewPassword, setShowForgotNewPassword] = useState(false);
  const [showForgotConfirmPassword, setShowForgotConfirmPassword] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotTimer, setForgotTimer] = useState(300); // 5 min = 300s
  const [forgotTimerActive, setForgotTimerActive] = useState(false);

  const getPasswordRules = (pwd = '') => ({
    minLength: (pwd || '').length >= 8,
    uppercase: /[A-Z]/.test(pwd || ''),
    lowercase: /[a-z]/.test(pwd || ''),
    specialChar: /[^a-zA-Z0-9]/.test(pwd || ''),
    get isValid() {
      return this.minLength && this.uppercase && this.lowercase && this.specialChar;
    }
  });

  const regPasswordRules = getPasswordRules(password);
  const resetPasswordRules = getPasswordRules(forgotNewPassword);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/subscription-price`)
      .then(r => setSubPrice(r.data.price))
      .catch(() => setSubPrice(5));
  }, []);

  const handleGoogleCredentialResponse = useCallback(async (response) => {
    if (!response?.credential) {
      toast.error('Não foi possível obter a credencial do Google.');
      return;
    }
    setGoogleLoading(true);
    try {
      await loginWithGoogle(response.credential);
      toast.success('🎉 Login com Google realizado com sucesso!');
      navigate('/dashboard');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao autenticar com o Google. Tente novamente.';
      toast.error(msg);
    } finally {
      setGoogleLoading(false);
    }
  }, [loginWithGoogle, navigate]);

  useEffect(() => {
    const renderGoogleBtn = () => {
      if (window.google?.accounts?.id && GOOGLE_CLIENT_ID) {
        try {
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleGoogleCredentialResponse,
            auto_select: false,
            use_fedcm_for_prompt: false,
          });

          const btnContainer = document.getElementById('google-btn-container');
          if (btnContainer) {
            btnContainer.innerHTML = '';
            const parentWidth = btnContainer.parentElement?.clientWidth || btnContainer.clientWidth || 240;
            const targetWidth = Math.min(400, Math.max(200, Math.floor(parentWidth)));

            window.google.accounts.id.renderButton(btnContainer, {
              type: 'standard',
              theme: 'outline',
              size: 'large',
              text: mode === 'register' ? 'signup_with' : 'signin_with',
              shape: 'rectangular',
              logo_alignment: 'left',
              width: targetWidth,
            });
          }
        } catch (err) {
          console.warn('Erro ao inicializar Google Sign-in:', err);
        }
      }
    };

    if (window.google?.accounts?.id) {
      renderGoogleBtn();
    } else {
      const timer = setInterval(() => {
        if (window.google?.accounts?.id) {
          renderGoogleBtn();
          clearInterval(timer);
        }
      }, 300);
      return () => clearInterval(timer);
    }

    window.addEventListener('resize', renderGoogleBtn);
    return () => window.removeEventListener('resize', renderGoogleBtn);
  }, [mode, handleGoogleCredentialResponse]);

  const handleManualGoogleLogin = () => {
    if (!GOOGLE_CLIENT_ID) {
      toast.error('Configure a variável REACT_APP_GOOGLE_CLIENT_ID no ambiente.');
      return;
    }
    if (window.google?.accounts?.id) {
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          console.log('Google prompt not displayed/skipped');
        }
      });
    } else {
      toast.error('SDK do Google ainda está carregando. Tente novamente.');
    }
  };

  const priceText = subPrice == null ? '5,00' : Number(subPrice).toFixed(2).replace('.', ',');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Preencha todos os campos obrigatórios.');
      return;
    }

    if (mode === 'register') {
      if (!name.trim()) {
        toast.error('Por favor, informe seu nome.');
        return;
      }
      if (!regPasswordRules.minLength) {
        toast.error('A senha deve ter no mínimo 8 caracteres.');
        return;
      }
      if (!regPasswordRules.uppercase) {
        toast.error('A senha deve conter no mínimo 1 letra maiúscula.');
        return;
      }
      if (!regPasswordRules.lowercase) {
        toast.error('A senha deve conter no mínimo 1 letra minúscula.');
        return;
      }
      if (!regPasswordRules.specialChar) {
        toast.error('A senha deve conter no mínimo 1 caractere especial (ex: !@#$%&*).');
        return;
      }
    } else {
      if (!password) {
        toast.error('Informe sua senha.');
        return;
      }
    }

    setLoading(true);
    try {
      if (mode === 'register') {
        await registerWithEmail(name, email, password);
        toast.success('🎉 Conta criada com sucesso! Você ganhou 10 dias de teste grátis!');
        navigate('/dashboard');
      } else {
        await loginWithEmail(email, password);
        toast.success('Bem-vindo(a) de volta!');
        navigate('/dashboard');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao realizar autenticação. Verifique os dados.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let interval = null;
    if (forgotTimerActive && forgotTimer > 0) {
      interval = setInterval(() => {
        setForgotTimer((prev) => prev - 1);
      }, 1000);
    } else if (forgotTimer === 0) {
      setForgotTimerActive(false);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [forgotTimerActive, forgotTimer]);

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleOpenForgotModal = () => {
    setForgotStep(1);
    setForgotEmail(email || '');
    setForgotCode('');
    setForgotNewPassword('');
    setForgotConfirmPassword('');
    setShowForgotNewPassword(false);
    setShowForgotConfirmPassword(false);
    setForgotTimer(300);
    setForgotTimerActive(false);
    setShowForgotModal(true);
  };

  const handleCloseForgotModal = () => {
    setShowForgotModal(false);
    setForgotStep(1);
    setForgotCode('');
    setForgotNewPassword('');
    setForgotConfirmPassword('');
    setForgotTimerActive(false);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    const cleanEmail = forgotEmail.trim().toLowerCase();
    if (!cleanEmail) {
      toast.error('Informe o seu e-mail.');
      return;
    }
    setForgotLoading(true);
    try {
      const res = await forgotPassword(cleanEmail);
      toast.success(res?.message || 'Código de recuperação enviado para o seu e-mail!');
      setForgotStep(2);
      setForgotTimer(300);
      setForgotTimerActive(true);
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || 'Não foi possível solicitar a recuperação. Tente novamente.';
      toast.error(msg);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    const cleanEmail = forgotEmail.trim().toLowerCase();
    const cleanCode = forgotCode.trim();

    if (!cleanCode) {
      toast.error('Informe o código de 6 dígitos recebido por e-mail.');
      return;
    }
    if (cleanCode.length < 6) {
      toast.error('O código de verificação deve conter 6 dígitos.');
      return;
    }
    if (!forgotNewPassword) {
      toast.error('Informe a nova senha.');
      return;
    }
    if (!resetPasswordRules.minLength) {
      toast.error('A nova senha deve ter no mínimo 8 caracteres.');
      return;
    }
    if (!resetPasswordRules.uppercase) {
      toast.error('A nova senha deve conter no mínimo 1 letra maiúscula.');
      return;
    }
    if (!resetPasswordRules.lowercase) {
      toast.error('A nova senha deve conter no mínimo 1 letra minúscula.');
      return;
    }
    if (!resetPasswordRules.specialChar) {
      toast.error('A nova senha deve conter no mínimo 1 caractere especial (ex: !@#$%&*).');
      return;
    }
    if (forgotNewPassword !== forgotConfirmPassword) {
      toast.error('As senhas não coincidem. Digite a mesma senha nos dois campos.');
      return;
    }

    setForgotLoading(true);
    try {
      const res = await resetPassword(cleanEmail, cleanCode, forgotNewPassword);
      toast.success(res?.message || 'Senha redefinida com sucesso!');
      handleCloseForgotModal();
      setEmail(cleanEmail);
      setPassword('');
      setMode('login');
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || 'Erro ao redefinir a senha. Verifique o código e tente novamente.';
      toast.error(msg);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResendForgotCode = async () => {
    const cleanEmail = forgotEmail.trim().toLowerCase();
    if (!cleanEmail) {
      toast.error('Informe o seu e-mail.');
      return;
    }
    setForgotLoading(true);
    try {
      const res = await forgotPassword(cleanEmail);
      toast.success(res?.message || 'Novo código de 6 dígitos enviado para o seu e-mail!');
      setForgotTimer(300);
      setForgotTimerActive(true);
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || 'Não foi possível reenviar o código.';
      toast.error(msg);
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col justify-center items-center p-0 md:p-3 lg:p-4 font-sans no-scrollbar">
      {/* Main Container Card */}
      <div className="landing-scale-card w-full max-w-7xl bg-white rounded-none md:rounded-3xl shadow-2xl overflow-hidden flex flex-col lg:flex-row border border-slate-200/80">
        
        {/* ================= LEFT / SHOWCASE SECTION (75% on desktop) ================= */}
        <div className="w-full lg:w-[72%] xl:w-[75%] bg-gradient-to-br from-[#071330] via-[#0C1E4E] to-[#122B68] text-white p-6 sm:p-10 lg:p-14 flex flex-col justify-between relative overflow-hidden">
          
          {/* Subtle Ambient Glow and Circuit Background Elements */}
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute inset-0 bg-[radial-gradient(#38bdf8_1px,transparent_1px)] [background-size:24px_24px] opacity-10 pointer-events-none" />

          {/* Top Brand Header */}
          <div className="relative z-10 flex flex-col sm:flex-row items-center sm:items-start gap-4 text-center sm:text-left mb-8">
            <div className="w-14 h-14 bg-gradient-to-tr from-blue-600 to-cyan-400 rounded-2xl flex items-center justify-center shadow-lg shadow-cyan-500/25 border border-cyan-300/30">
              <DollarSign className="text-white w-8 h-8 stroke-[2.5]" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white flex items-center justify-center sm:justify-start gap-2">
                Finance
              </h1>
              <p className="text-cyan-200/90 text-lg sm:text-xl font-medium tracking-wide">
                Gestão Financeira Inteligente
              </p>
            </div>
          </div>

          {/* Center Graphic Showcase: Cards & Serafina Mascot */}
          <div className="relative z-10 my-4 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            
            {/* Left Graph Cards */}
            <div className="md:col-span-6 space-y-4">
              
              {/* Card 1: Receitas, Despesas, Saldo with Curved Line */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 sm:p-5 border border-white/15 shadow-xl hover:bg-white/[0.12] transition-all">
                <div className="grid grid-cols-3 gap-2 border-b border-white/10 pb-3 mb-3">
                  <div className="flex flex-col min-w-0">
                    <div className="flex items-center gap-1.5 text-[11px] sm:text-xs text-cyan-300 font-medium">
                      <span className="w-2 h-2 rounded-full bg-cyan-400 shrink-0"></span>
                      <span className="truncate">Receitas</span>
                    </div>
                    <span className="text-white font-bold text-xs sm:text-sm mt-0.5 whitespace-nowrap">R$ 3.269,54</span>
                  </div>
                  <div className="flex flex-col min-w-0">
                    <div className="flex items-center gap-1.5 text-[11px] sm:text-xs text-red-300 font-medium">
                      <span className="w-2 h-2 rounded-full bg-red-400 shrink-0"></span>
                      <span className="truncate">Despesas</span>
                    </div>
                    <span className="text-white font-bold text-xs sm:text-sm mt-0.5 whitespace-nowrap">R$ 2.705,72</span>
                  </div>
                  <div className="flex flex-col min-w-0">
                    <div className="flex items-center gap-1.5 text-[11px] sm:text-xs text-emerald-300 font-medium">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0"></span>
                      <span className="truncate">Saldo</span>
                    </div>
                    <span className="text-white font-bold text-xs sm:text-sm mt-0.5 whitespace-nowrap">R$ 563,82</span>
                  </div>
                </div>

                {/* SVG Curve Chart */}
                <div className="h-32 w-full pt-2">
                  <svg viewBox="0 0 300 100" className="w-full h-full overflow-visible">
                    {/* Grid lines */}
                    <line x1="0" y1="20" x2="300" y2="20" stroke="rgba(255,255,255,0.1)" strokeDasharray="3 3" />
                    <line x1="0" y1="50" x2="300" y2="50" stroke="rgba(255,255,255,0.1)" strokeDasharray="3 3" />
                    <line x1="0" y1="80" x2="300" y2="80" stroke="rgba(255,255,255,0.1)" strokeDasharray="3 3" />

                    {/* Gradient Area under curve */}
                    <defs>
                      <linearGradient id="curveGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="#38bdf8" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M 10 80 Q 80 80 140 78 T 220 70 T 280 15 L 280 80 L 10 80 Z"
                      fill="url(#curveGradient)"
                    />
                    {/* Curve Line */}
                    <path
                      d="M 10 80 Q 80 80 140 78 T 220 70 T 280 15"
                      fill="none"
                      stroke="#38bdf8"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                    />
                    {/* Points */}
                    <circle cx="10" cy="80" r="4" fill="#0284c7" stroke="#fff" strokeWidth="1.5" />
                    <circle cx="75" cy="80" r="4" fill="#0284c7" stroke="#fff" strokeWidth="1.5" />
                    <circle cx="140" cy="78" r="4" fill="#0284c7" stroke="#fff" strokeWidth="1.5" />
                    <circle cx="210" cy="70" r="4" fill="#0284c7" stroke="#fff" strokeWidth="1.5" />
                    <circle cx="280" cy="15" r="5" fill="#38bdf8" stroke="#fff" strokeWidth="2" className="animate-pulse" />
                  </svg>
                  <div className="flex justify-between text-[11px] text-slate-300 font-medium px-2 mt-1">
                    <span>Jan</span>
                    <span>Fev</span>
                    <span>Mar</span>
                    <span>Abr</span>
                    <span>Mai</span>
                  </div>
                </div>
              </div>

              {/* Card 2: Saldo Mensal Bar Chart */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 sm:p-5 border border-white/15 shadow-xl hover:bg-white/[0.12] transition-all">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-bold text-white tracking-wide">Saldo Mensal</span>
                  <span className="text-xs bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded-full font-medium">
                    +14.8% este mês
                  </span>
                </div>
                
                {/* Bar chart mockup */}
                <div className="flex items-end justify-between gap-3 h-24 pt-2 px-3 border-b border-white/10">
                  <div className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full bg-cyan-500/40 rounded-t-md h-6" />
                  </div>
                  <div className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full bg-cyan-500/55 rounded-t-md h-10" />
                  </div>
                  <div className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full bg-cyan-500/70 rounded-t-md h-14" />
                  </div>
                  <div className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full bg-cyan-500/85 rounded-t-md h-18" />
                  </div>
                  <div className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full bg-gradient-to-t from-cyan-400 to-blue-400 rounded-t-md h-22 shadow-lg shadow-cyan-500/40" />
                  </div>
                </div>
                <div className="flex justify-between text-[11px] text-slate-300 font-medium px-3 mt-1">
                  <span>Jan</span>
                  <span>Fev</span>
                  <span>Mar</span>
                  <span>Abr</span>
                  <span>Mai</span>
                </div>
              </div>

            </div>

            {/* Right Mascot Visual (Serafina AI) */}
            <div className="md:col-span-6 flex flex-col items-center justify-center relative">
              <div className="relative group">
                <div className="absolute inset-0 bg-cyan-400/20 rounded-full blur-2xl group-hover:bg-cyan-400/30 transition-all"></div>
                <img 
                  src="/images/serafina.png" 
                  alt="Serafina - Assistente de IA Finance" 
                  className="w-56 sm:w-64 md:w-72 lg:w-80 object-contain drop-shadow-2xl relative z-10 transform group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>
          </div>

          {/* Bottom Slogan & Feature Highlights */}
          <div className="relative z-10 pt-6 border-t border-white/10 mt-4">
            <h2 className="text-center text-lg sm:text-xl lg:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-white to-cyan-200 mb-6">
              Planeje suas finanças com Inteligência Artificial
            </h2>

            {/* 4 Feature Badges Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0 text-cyan-300">
                  <TrendingUp size={18} />
                </div>
                <div className="text-left">
                  <p className="text-xs font-bold text-white">Registros</p>
                  <p className="text-[10px] text-slate-300">Parcelas e controle</p>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0 text-cyan-300">
                  <Target size={18} />
                </div>
                <div className="text-left">
                  <p className="text-xs font-bold text-white">Metas</p>
                  <p className="text-[10px] text-slate-300">Progresso real</p>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0 text-cyan-300">
                  <PieChart size={18} />
                </div>
                <div className="text-left">
                  <p className="text-xs font-bold text-white">Dashboard</p>
                  <p className="text-[10px] text-slate-300">Relatórios & PDF</p>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0 text-cyan-300">
                  <Bot size={18} />
                </div>
                <div className="text-left">
                  <p className="text-xs font-bold text-white">Serafina IA</p>
                  <p className="text-[10px] text-slate-300">Insights precisos</p>
                </div>
              </div>
            </div>

            {/* Copyright Notice & Version */}
            <div className="text-center mt-5 space-y-0.5">
              <p className="text-[11px] text-slate-400 font-normal tracking-wide">
                © 2026 Finance · Todos os direitos reservados
              </p>
              <p className="text-[11px] text-slate-400 font-normal tracking-wide">
                v3.1.2 Registry Filter
              </p>
            </div>
          </div>

        </div>


        {/* ================= RIGHT / LOGIN & REGISTER SECTION (25-28% on desktop) ================= */}
        <div className="w-full lg:w-[28%] xl:w-[25%] bg-white p-6 sm:p-8 flex flex-col justify-center relative">
          
          {/* Header Switcher */}
          <div className="mb-6">
            <div className="flex bg-slate-100 p-1 rounded-xl mb-4">
              <button
                type="button"
                onClick={() => setMode('login')}
                className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all ${
                  mode === 'login'
                    ? 'bg-blue-900 text-white shadow-sm'
                    : 'text-slate-600 hover:text-blue-900'
                }`}
              >
                Entrar
              </button>
              <button
                type="button"
                onClick={() => setMode('register')}
                className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all flex items-center justify-center gap-1 ${
                  mode === 'register'
                    ? 'bg-blue-900 text-white shadow-sm'
                    : 'text-slate-600 hover:text-blue-900'
                }`}
              >
                <span>Criar Conta</span>
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              </button>
            </div>

            {/* Trial Promo Banner */}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-3 text-center mb-4">
              <div className="flex items-center justify-center gap-1.5 text-emerald-800 font-bold text-xs">
                <Sparkles size={14} className="text-emerald-600" />
                <span>10 DIAS TOTALMENTE GRÁTIS</span>
              </div>
              <p className="text-[11px] text-emerald-700 mt-0.5">
                Experimente todos os recursos sem compromisso.
              </p>
            </div>

            {/* Google Sign-in Button */}
            <div className="space-y-3">
              <div id="google-btn-container" className="w-full flex justify-center min-h-[40px] overflow-hidden max-w-full [&>div]:!w-full [&_iframe]:!w-full"></div>
              
              {/* Fallback button if GSI container is not rendered yet */}
              {(!GOOGLE_CLIENT_ID || !window.google?.accounts?.id) && (
                <button
                  type="button"
                  onClick={handleManualGoogleLogin}
                  disabled={googleLoading}
                  className="w-full flex items-center justify-center gap-2.5 py-2.5 px-4 bg-white hover:bg-slate-50 border border-slate-300 hover:border-slate-400 rounded-xl text-xs font-semibold text-slate-700 shadow-sm transition-all"
                >
                  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                  </svg>
                  <span>{googleLoading ? 'Conectando ao Google...' : mode === 'register' ? 'Criar conta com Google' : 'Continuar com o Google'}</span>
                </button>
              )}

              {/* Divider */}
              <div className="relative py-1">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-2.5 text-slate-400 text-[11px] uppercase tracking-wider font-medium">
                    ou com e-mail
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Email / Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            
            {mode === 'register' && (
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block">
                  Nome Completo
                </label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Digite seu nome completo"
                    className="w-full pb-2 pt-1 border-b-2 border-slate-300 focus:border-blue-900 text-sm text-slate-800 outline-none bg-transparent transition-colors placeholder:text-slate-400"
                  />
                  <User className="absolute right-1 bottom-2.5 text-slate-400 w-4 h-4 pointer-events-none" />
                </div>
              </div>
            )}

            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block">
                E-mail
              </label>
              <div className="relative">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Digite seu email"
                  className="w-full pb-2 pt-1 border-b-2 border-slate-300 focus:border-blue-900 text-sm text-slate-800 outline-none bg-transparent transition-colors placeholder:text-slate-400"
                />
                <Mail className="absolute right-1 bottom-2.5 text-slate-400 w-4 h-4 pointer-events-none" />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block">
                Senha
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite sua senha"
                  className="w-full pb-2 pt-1 pr-8 border-b-2 border-slate-300 focus:border-blue-900 text-sm text-slate-800 outline-none bg-transparent transition-colors placeholder:text-slate-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-1 bottom-2.5 text-slate-500 hover:text-blue-900 transition-colors focus:outline-none"
                  aria-label={showPassword ? 'Ocultar senha' : 'Exibir senha'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {mode === 'register' && (
                <div className="mt-2.5 p-2.5 rounded-xl bg-slate-50 border border-slate-200/80 text-[11px] space-y-1 animate-in fade-in duration-200">
                  <p className="font-bold text-slate-600 mb-1 text-[11px]">Requisitos da senha:</p>
                  <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                    <div className={`flex items-center gap-1.5 transition-colors ${regPasswordRules.minLength ? 'text-emerald-700 font-semibold' : 'text-slate-500'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${regPasswordRules.minLength ? 'bg-emerald-500 ring-2 ring-emerald-300/60' : 'bg-slate-300'}`} />
                      <span>Mínimo 8 caracteres</span>
                    </div>
                    <div className={`flex items-center gap-1.5 transition-colors ${regPasswordRules.uppercase ? 'text-emerald-700 font-semibold' : 'text-slate-500'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${regPasswordRules.uppercase ? 'bg-emerald-500 ring-2 ring-emerald-300/60' : 'bg-slate-300'}`} />
                      <span>1 letra maiúscula</span>
                    </div>
                    <div className={`flex items-center gap-1.5 transition-colors ${regPasswordRules.lowercase ? 'text-emerald-700 font-semibold' : 'text-slate-500'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${regPasswordRules.lowercase ? 'bg-emerald-500 ring-2 ring-emerald-300/60' : 'bg-slate-300'}`} />
                      <span>1 letra minúscula</span>
                    </div>
                    <div className={`flex items-center gap-1.5 transition-colors ${regPasswordRules.specialChar ? 'text-emerald-700 font-semibold' : 'text-slate-500'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${regPasswordRules.specialChar ? 'bg-emerald-500 ring-2 ring-emerald-300/60' : 'bg-slate-300'}`} />
                      <span>1 caracter especial</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white py-3 rounded-xl font-bold shadow-lg shadow-blue-900/20 transition-all mt-6 text-sm"
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Processando...
                </span>
              ) : mode === 'register' ? (
                'Criar Conta & Ativar 10 Dias'
              ) : (
                'Login'
              )}
            </Button>
          </form>

          {/* Footer Links */}
          <div className="mt-5 flex items-center justify-between text-xs text-slate-500">
            {mode === 'login' ? (
              <>
                <button
                  type="button"
                  onClick={handleOpenForgotModal}
                  className="hover:text-blue-900 hover:underline transition-colors"
                >
                  Esqueceu a senha?
                </button>
                <button
                  type="button"
                  onClick={() => setMode('register')}
                  className="font-bold text-blue-900 hover:underline transition-colors"
                >
                  Criar conta
                </button>
              </>
            ) : (
              <div className="w-full text-center">
                <span>Já possui uma conta? </span>
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="font-bold text-blue-900 hover:underline transition-colors"
                >
                  Fazer login
                </button>
              </div>
            )}
          </div>

          {/* WhatsApp Support Contact */}
          <div className="mt-5 pt-3 border-t border-slate-100 text-center">
            <p className="text-xs font-semibold text-slate-500 mb-2">Contato Suporte</p>
            <a
              href="https://wa.me/5582999744175?text=Ol%C3%A1!%20Gostaria%20de%20tirar%20d%C3%BAvidas%20sobre%20o%20Finance."
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 py-2 px-3 bg-blue-50/70 hover:bg-blue-100/80 border border-blue-200/60 rounded-xl text-xs font-semibold text-[#1E3A8A] hover:text-[#1E40AF] transition-all group"
            >
              <FaWhatsapp className="text-[#1E3A8A] text-base group-hover:scale-110 transition-transform" />
              <span>(82) 9 9974-4175</span>
            </a>
          </div>

          {/* Plan Info */}
          <div className="mt-6 pt-4 border-t border-slate-100 text-center">
            <p className="text-[11px] text-slate-400">
              Após os 10 dias grátis, apenas <span className="font-bold text-slate-600">R$ {priceText}/mês</span>. Cancele quando quiser.
            </p>
          </div>
        </div>

      </div>

      {/* ================= FORGOT / RESET PASSWORD MODAL ================= */}
      {showForgotModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200 animate-in fade-in zoom-in-95 duration-200">
            
            {forgotStep === 1 ? (
              <>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-900 border border-blue-100">
                    <KeyRound className="w-5 h-5 text-[#1E3A8A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#1E3A8A]">Recuperar Senha</h3>
                    <p className="text-xs text-slate-500">Etapa 1 de 2 · Identificação</p>
                  </div>
                </div>

                <p className="text-sm text-slate-600 mb-5">
                  Informe seu e-mail cadastrado para receber um código temporário de 6 dígitos válido por 5 minutos.
                </p>

                <form onSubmit={handleForgotSubmit} className="space-y-4">
                  <div>
                    <label className="text-xs font-bold uppercase text-slate-600 block mb-1">
                      E-mail da sua conta
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        required
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="seu@email.com"
                        className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-xl text-sm outline-none focus:border-blue-900 focus:ring-2 focus:ring-blue-900/20 transition-all"
                      />
                      <Mail className="absolute left-3.5 top-3 w-4 h-4 text-slate-400" />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCloseForgotModal}
                      className="flex-1 border-slate-300 text-slate-700 hover:bg-slate-50"
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      disabled={forgotLoading}
                      className="flex-1 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white shadow-md shadow-blue-900/20"
                    >
                      {forgotLoading ? 'Enviando...' : 'Enviar Código'}
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center text-blue-900 border border-blue-100">
                      <Lock className="w-4 h-4 text-[#1E3A8A]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-[#1E3A8A]">Redefinir Senha</h3>
                      <p className="text-xs text-slate-500">Etapa 2 de 2 · Validação</p>
                    </div>
                  </div>

                  {/* Timer Badge */}
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                    forgotTimer > 0 
                      ? 'bg-blue-50 text-[#1E3A8A] border border-blue-200/80' 
                      : 'bg-red-50 text-red-600 border border-red-200'
                  }`}>
                    <Clock className="w-3.5 h-3.5" />
                    <span>{forgotTimer > 0 ? formatTimer(forgotTimer) : 'Expirado'}</span>
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3 mb-4 text-xs text-slate-600">
                  <p>
                    Enviamos o código de 6 dígitos para <strong className="text-slate-900 font-semibold">{forgotEmail}</strong>.
                  </p>
                </div>

                <form onSubmit={handleResetPasswordSubmit} className="space-y-3.5">
                  {/* Token Field */}
                  <div>
                    <label className="text-xs font-bold uppercase text-slate-600 block mb-1">
                      Código de 6 dígitos
                    </label>
                    <input
                      type="text"
                      required
                      maxLength={6}
                      value={forgotCode}
                      onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="123456"
                      autoFocus
                      className="w-full text-center tracking-[0.4em] font-mono text-xl font-bold py-2.5 border border-slate-300 rounded-xl outline-none focus:border-blue-900 focus:ring-2 focus:ring-blue-900/20 bg-white"
                    />
                  </div>

                  {/* New Password Field */}
                  <div>
                    <label className="text-xs font-bold uppercase text-slate-600 block mb-1">
                      Nova Senha
                    </label>
                    <div className="relative">
                      <input
                        type={showForgotNewPassword ? 'text' : 'password'}
                        required
                        minLength={6}
                        value={forgotNewPassword}
                        onChange={(e) => setForgotNewPassword(e.target.value)}
                        placeholder="Mínimo 6 caracteres"
                        className="w-full pl-3 pr-10 py-2 border border-slate-300 rounded-xl text-sm outline-none focus:border-blue-900 focus:ring-2 focus:ring-blue-900/20"
                      />
                      <button
                        type="button"
                        onClick={() => setShowForgotNewPassword(!showForgotNewPassword)}
                        className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600"
                      >
                        {showForgotNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>

                    <div className="mt-1.5 p-2.5 rounded-xl bg-slate-50 border border-slate-200/80 text-[11px] space-y-1">
                      <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                        <div className={`flex items-center gap-1.5 transition-colors ${resetPasswordRules.minLength ? 'text-emerald-700 font-semibold' : 'text-slate-500'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${resetPasswordRules.minLength ? 'bg-emerald-500 ring-2 ring-emerald-300/60' : 'bg-slate-300'}`} />
                          <span>Mínimo 8 caracteres</span>
                        </div>
                        <div className={`flex items-center gap-1.5 transition-colors ${resetPasswordRules.uppercase ? 'text-emerald-700 font-semibold' : 'text-slate-500'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${resetPasswordRules.uppercase ? 'bg-emerald-500 ring-2 ring-emerald-300/60' : 'bg-slate-300'}`} />
                          <span>1 letra maiúscula</span>
                        </div>
                        <div className={`flex items-center gap-1.5 transition-colors ${resetPasswordRules.lowercase ? 'text-emerald-700 font-semibold' : 'text-slate-500'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${resetPasswordRules.lowercase ? 'bg-emerald-500 ring-2 ring-emerald-300/60' : 'bg-slate-300'}`} />
                          <span>1 letra minúscula</span>
                        </div>
                        <div className={`flex items-center gap-1.5 transition-colors ${resetPasswordRules.specialChar ? 'text-emerald-700 font-semibold' : 'text-slate-500'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${resetPasswordRules.specialChar ? 'bg-emerald-500 ring-2 ring-emerald-300/60' : 'bg-slate-300'}`} />
                          <span>1 caracter especial</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Confirm New Password Field */}
                  <div>
                    <label className="text-xs font-bold uppercase text-slate-600 block mb-1">
                      Confirmar Nova Senha
                    </label>
                    <div className="relative">
                      <input
                        type={showForgotConfirmPassword ? 'text' : 'password'}
                        required
                        minLength={6}
                        value={forgotConfirmPassword}
                        onChange={(e) => setForgotConfirmPassword(e.target.value)}
                        placeholder="Repita a nova senha"
                        className="w-full pl-3 pr-10 py-2 border border-slate-300 rounded-xl text-sm outline-none focus:border-blue-900 focus:ring-2 focus:ring-blue-900/20"
                      />
                      <button
                        type="button"
                        onClick={() => setShowForgotConfirmPassword(!showForgotConfirmPassword)}
                        className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600"
                      >
                        {showForgotConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-2.5 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setForgotStep(1)}
                      className="border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center gap-1.5 px-3"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" />
                      Voltar
                    </Button>
                    <Button
                      type="submit"
                      disabled={forgotLoading || forgotTimer === 0}
                      className="flex-1 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white shadow-md shadow-blue-900/20"
                    >
                      {forgotLoading ? 'Salvando...' : 'Redefinir Senha'}
                    </Button>
                  </div>

                  {/* Resend Code Link */}
                  <div className="text-center pt-2">
                    <button
                      type="button"
                      onClick={handleResendForgotCode}
                      disabled={forgotLoading}
                      className="text-xs text-[#1E3A8A] hover:text-[#1E40AF] font-medium hover:underline inline-flex items-center gap-1 transition-colors"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Não recebeu ou expirou? Reenviar código
                    </button>
                  </div>
                </form>
              </>
            )}

          </div>
        </div>
      )}
    </div>
  );
};
